import { Component, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { OverlayContainer } from '@angular/cdk/overlay';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import {
  UserService,
  BroadcastService,
  Event,
  LicensesPageService,
  UserAuth
} from '@nrwl/common-lib';
import { LocaleService } from '../services/locale/locale.service'; //TODO - localeService should be moved to @nrwl/common-lib
import { MainEntityService } from '../services/entity/main.entity.service';
import { environment } from 'apps/fusion-ui/src/environments/environment';
import { ControllerApiService } from '@nrwl/test-setup';
const dThemeClass = 'dark-theme';
@Component({
  selector: 'app-root',
  template: '<router-outlet></router-outlet>'
})
export class HomeComponent implements OnInit, OnDestroy {
  @HostBinding(`class.${dThemeClass}`) isDarkTheme;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly broadcastService: BroadcastService,
    private readonly userService: UserService,
    private readonly localeService: LocaleService,
    private overlayContainer: OverlayContainer,
    private readonly router: Router,
    private readonly entityService: MainEntityService,
    private readonly http: HttpClient,
    private readonly title: Title,
    private licenseService: LicensesPageService,
    public userAuth: UserAuth,
    private readonly controllerApiService: ControllerApiService
  ) {}

  ngOnInit(): void {
    this.userService.brandingEvents.next({ dynamicBranding: true });
    //this.localeService.updateLocale(null);
    this.userService.profileEvents.pipe(takeUntil(this.unsubscribe)).subscribe(event => {
      if (event.event === 'user-prefs') {
        if (event.username === this.userService.getUserName()) {
          if (this.isDarkTheme !== (event.prefs.userSettings.theme === 'dark')) {
            this.isDarkTheme = event.prefs.userSettings.theme === 'dark';
            this.setThemeToOverlayContainer();
          }
        }
      } else if (event.event === 'logout') {
        this.entityService.clearEntities();
        // it is fine for logout to reset URL
        this.router.navigate(['logout']);
      }
      if (event.event === 'login') {
        this.loadServerConfig();
        this.getAvailableLicenses();
        this.loadUiConfig();
      }
    });
    if (this.hasUserToken()) {
      this.loadServerConfig();
      this.getAvailableLicenses();
      this.loadUiConfig();
    }
    this.userService.setEnvironment(environment);
    this._themeInit();
    this._broadcastSubscribe();
    this.userService.getUserAuth();
  }

  loadServerConfig(): void {
    this.controllerApiService
      .getServerConfig()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.controllerApiService.serverConfig = response.data;
      });
  }

  hasUserToken(): boolean {
    const user = sessionStorage.getItem(this.userAuth.STORAGE_KEY);
    if (user) {
      const userJson = JSON.parse(user);
      if (userJson.token) {
        return true;
      }
    }
    return false;
  }

  loadUiConfig(): void {
    this.http
      .get('/en/ui-config.json', {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      })
      .subscribe(
        success => {
          this.userService.setBrandShortName(success['productname_short']);
          this.userService.setBrandingName(success['productname_long']);
          this.title.setTitle(this.userService.getBrandingName());
        },
        //eslint-disable-next-line
        err => {}
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getAvailableLicenses(): void {
    this.licenseService
      .getLicenseCount()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        licenses => {
          if (licenses?.length) {
            this.licenseService.setLicenseData(licenses);
          }
          licenses.map(item => {
            if (item.featureName === 'ACT-WEB') {
              this.licenseService.setIsActWebLicense(true);
            }
          });
        },
        () => {}
      );
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private setThemeToOverlayContainer() {
    if (this.isDarkTheme) {
      this.overlayContainer.getContainerElement().classList.add(dThemeClass);
    } else {
      this.overlayContainer.getContainerElement().classList.remove(dThemeClass);
    }
  }
  private _themeInit(): void {
    this.isDarkTheme = this.userService.getDefaultTheme() === 'dark';
    this.setThemeToOverlayContainer();
  }

  private _broadcastSubscribe(): void {
    this.broadcastService.eventSubject
      .pipe(
        takeUntil(this.unsubscribe),
        filter((event: Event) => event.target === 'appComponent')
      )
      .subscribe((event: Event) => {
        switch (event.name) {
          case 'changeTheme': {
            if (event.body.userName === this.userService.getUserName()) {
              this.userService.setThemeToStorage(event.body.theme);
              this.isDarkTheme = event.body.theme === 'dark';
            }

            if (event.body.theme === 'dark') {
              this.overlayContainer.getContainerElement().classList.add(dThemeClass);
              if (event.body.userName) {
                this.userService.setTheme('dark', event.body.userName);
              }
            } else {
              this.overlayContainer.getContainerElement().classList.remove(dThemeClass);
              this.userService.setTheme('light', event.body.userName);
            }
            break;
          }
        }
      });
  }
}
